import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Phone, X, PhoneCall, Loader2, CheckCircle, 
  AlertCircle, User, FileText
} from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CallModal = ({ isOpen, onClose, lead }) => {
  const [scripts, setScripts] = useState({});
  const [selectedScript, setSelectedScript] = useState('general');
  const [calling, setCalling] = useState(false);
  const [callStatus, setCallStatus] = useState(null); // null, 'calling', 'success', 'error'
  const [callResult, setCallResult] = useState(null);

  useEffect(() => {
    if (isOpen) {
      fetchScripts();
      setCallStatus(null);
      setCallResult(null);
    }
  }, [isOpen]);

  const fetchScripts = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/vapi/scripts`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setScripts(data.scripts || {});
    } catch (error) {
      console.error('Error fetching scripts:', error);
    }
  };

  const initiateCall = async () => {
    setCalling(true);
    setCallStatus('calling');

    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_URL}/api/vapi/call`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          lead_id: lead.id,
          script_type: selectedScript
        })
      });

      const data = await res.json();

      if (res.ok) {
        setCallStatus('success');
        setCallResult(data);
        toast.success(`Calling ${lead.name}...`);
      } else {
        setCallStatus('error');
        setCallResult({ error: data.detail || 'Failed to initiate call' });
        toast.error(data.detail || 'Failed to initiate call');
      }
    } catch (error) {
      setCallStatus('error');
      setCallResult({ error: 'Failed to connect. Please check your internet connection.' });
      toast.error('Failed to initiate call');
    } finally {
      setCalling(false);
    }
  };

  const formatPhone = (phone) => {
    if (!phone) return 'No phone number';
    // Format for display
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `+91 ${cleaned.slice(0,5)} ${cleaned.slice(5)}`;
    }
    return phone;
  };

  if (!isOpen || !lead) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-[#0a3d2e] border border-emerald-700/50 rounded-2xl p-6 w-full max-w-md shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-white flex items-center gap-2">
              <PhoneCall className="w-6 h-6 text-emerald-400" />
              AI Voice Call
            </h3>
            <button
              onClick={onClose}
              className="text-emerald-300/70 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Lead Info */}
          <div className="bg-emerald-900/30 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center text-white font-bold text-lg">
                {lead.name?.charAt(0) || 'L'}
              </div>
              <div>
                <h4 className="font-semibold text-white">{lead.name}</h4>
                <p className="text-emerald-300/70 text-sm flex items-center gap-1">
                  <Phone className="w-3 h-3" />
                  {formatPhone(lead.phone)}
                </p>
              </div>
            </div>
          </div>

          {/* Script Selection */}
          {callStatus === null && (
            <>
              <div className="mb-6">
                <label className="block text-sm font-medium text-emerald-300 mb-3">
                  Select Call Script
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {Object.entries(scripts).map(([key, script]) => (
                    <button
                      key={key}
                      onClick={() => setSelectedScript(key)}
                      className={`p-3 rounded-lg border text-left transition-all ${
                        selectedScript === key
                          ? 'bg-emerald-500/20 border-emerald-500 text-white'
                          : 'bg-emerald-900/30 border-emerald-700/50 text-emerald-300 hover:border-emerald-600'
                      }`}
                    >
                      <div className="font-medium text-sm">{script.name}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Script Preview */}
              {scripts[selectedScript] && (
                <div className="bg-emerald-900/20 rounded-lg p-4 mb-6 border border-emerald-700/30">
                  <div className="flex items-center gap-2 text-emerald-400 text-sm mb-2">
                    <FileText className="w-4 h-4" />
                    Script Preview
                  </div>
                  <p className="text-emerald-100/80 text-sm italic">
                    "{scripts[selectedScript].firstMessage.replace('{name}', lead.name)}"
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={onClose}
                  className="flex-1 border-emerald-700/50 text-emerald-300 hover:bg-emerald-900/30"
                >
                  Cancel
                </Button>
                <Button
                  onClick={initiateCall}
                  disabled={calling || !lead.phone}
                  className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-white"
                >
                  {calling ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Initiating...
                    </>
                  ) : (
                    <>
                      <Phone className="w-4 h-4 mr-2" />
                      Start AI Call
                    </>
                  )}
                </Button>
              </div>

              {!lead.phone && (
                <p className="text-yellow-400 text-sm mt-3 text-center">
                  This lead doesn't have a phone number.
                </p>
              )}
            </>
          )}

          {/* Calling Status */}
          {callStatus === 'calling' && (
            <div className="text-center py-8">
              <div className="relative inline-flex">
                <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <Phone className="w-10 h-10 text-emerald-400 animate-pulse" />
                </div>
                <span className="absolute inset-0 rounded-full animate-ping bg-emerald-500/30"></span>
              </div>
              <h4 className="text-xl font-semibold text-white mt-4">Calling {lead.name}...</h4>
              <p className="text-emerald-300/70 mt-2">AI agent is dialing the number</p>
            </div>
          )}

          {/* Success Status */}
          {callStatus === 'success' && (
            <div className="text-center py-8">
              <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto">
                <CheckCircle className="w-10 h-10 text-emerald-400" />
              </div>
              <h4 className="text-xl font-semibold text-white mt-4">Call Initiated!</h4>
              <p className="text-emerald-300/70 mt-2">{callResult?.message}</p>
              {callResult?.call_reference && (
                <p className="text-emerald-400/60 text-sm mt-2">
                  Reference: {callResult.call_reference.slice(0, 8)}...
                </p>
              )}
              <Button
                onClick={onClose}
                className="mt-6 bg-emerald-600 hover:bg-emerald-500 text-white"
              >
                Close
              </Button>
            </div>
          )}

          {/* Error Status */}
          {callStatus === 'error' && (
            <div className="text-center py-8">
              <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto">
                <AlertCircle className="w-10 h-10 text-red-400" />
              </div>
              <h4 className="text-xl font-semibold text-white mt-4">Call Failed</h4>
              <p className="text-red-300/70 mt-2 px-4 text-sm">
                {callResult?.error || 'Unable to initiate call. Please check settings.'}
              </p>
              {callResult?.error?.includes('international') && (
                <div className="mt-3 p-3 bg-yellow-500/10 rounded-lg mx-4">
                  <p className="text-yellow-300 text-xs">
                    💡 Tip: Contact support to enable international calling to India (+91)
                  </p>
                </div>
              )}
              <div className="flex gap-3 mt-6">
                <Button
                  variant="outline"
                  onClick={onClose}
                  className="flex-1 border-emerald-700/50 text-emerald-300"
                >
                  Close
                </Button>
                <Button
                  onClick={() => {
                    setCallStatus(null);
                    setCallResult(null);
                  }}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white"
                >
                  Try Again
                </Button>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CallModal;
